import React, {useEffect, useRef, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import {ErrorScreen, HeaderToolbar, Loading} from "../../components";
import Toolbar from "@material-ui/core/Toolbar";
import {useAuthUser} from "../../components/auth-provider";
import {AccountService, SkuService} from "../../services";
import FuseAnimate from "../../components/fuse-animate/fuse-animate";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import ausPhone from "../../utils/aus-phone";
import Button from "@material-ui/core/Button";
import {
    FormControl, FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    TextField
} from "@material-ui/core";
import useStore from "../../store/hooks";
import {fetchAccount, invalidateAccount} from "../../store/actions";
import Checkout from "../../components/checkout/checkout";
import {
    CheckCircleOutline,
    ErrorOutline,
    SentimentVeryDissatisfied,
    Visibility,
    VisibilityOff
} from "@material-ui/icons";
import externalLink from "../../utils/external-link";
import {Link} from "react-router-dom";
import {Controller, useForm} from "react-hook-form";
import {isValidMobile} from "../../utils/valid";
import clsx from "clsx";
import e164ify from "../../utils/e164";
import {totals} from "../../utils/checkout";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        marginTop: 16,
        justifyContent: "center",
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        width: '100%',
        maxWidth: 340,
        [theme.breakpoints.up('sm')]: {
            maxWidth: 360,
        },
    },
    skuButton: {
        textTransform: 'none',
        padding: 0,
    },
    formField: {
        marginBottom: 24,
    },
}));

const TransferServicePage = ({location, match}) => {
    const classes = useStyles();
    const serviceId = match.params.id;

    const [store, dispatch] = useStore()
    const {user} = useAuthUser()

    const [state, setState] = useState({
        view: 'start',          // current view
        sku: {
            id: '',
            name: '',
            type: '',
            taxRate: 0,
            taxInc: false,
            price: 0,
            meta: {
                description: '',
                inclusions: {
                    minutes: {
                        quantity: 0,
                        expiryDays: 0,
                    },
                }
            }
        },
        service: {
            id: 0,
            number: '',
            callCredits: 0,
        },
        purchase:       {},
        order:          {},
        termsAccepted:  false,
        isFetching:     false,
        error:          null,
        mobileNumber:   '',
        password:       '',
        showPassword:   false,
        rxAccountError: '',
        ticketId:       '',
    });

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    // Make sure the account is available in the store
    useEffect(()=> {
        dispatch(fetchAccount(user.accountId, store, dispatch))
    }, [user.accountId, store, dispatch])

    // Load SKUs on mount
    useEffect(() => {
        setState(state => ({...state, isFetching: true}));
        SkuService.getSku('SVC-PTTX-001')
            .then(sku => {
                setState(state => ({...state, sku, isFetching: false}));
            })
            .catch(error => {
                setState(state => ({...state, error, isFetching: false}));
            })
    }, []) // <- load on start

    // Set the selected service
    useEffect(() => {
        const services = store.account.services.filter(s => s.id.toString() === serviceId);
        if (services.length > 0) {
            setState(state => ({...state, service: services[0]}));
        }
    }, [serviceId, state.service, store.account])

    return (
        <div id="add-service" className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location={location.pathname}/>
            </AppBar>
            <Toolbar/>

            <main className={classes.content}>
                { state.isFetching || store.account.isFetching ? <Loading/> : null }
                { store.account.error && <ErrorScreen error={state.error}/> }
                { state.error && <ErrorScreen error={state.error}/> }
                { state.error === null && !state.isFetching && store.account.error === null && !store.account.isFetching ?
                    <ContentCard state={state} setState={setState} />
                    : null
                }
            </main>

        </div>
    );
}

const ContentCard = ({state, setState}) => {

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div id="transfer" className="flex flex-grow flex-col items-center">
                { state.view === 'start' ?
                    <ItemCard state={state} setState={setState} />
                    : null
                }
                { state.view === 'terms' ?
                    <ReceivingAccountCard state={state} setState={setState} />
                    : null
                }
                { state.view === 'checkout' ?
                    <CheckoutCard state={state} setState={setState} />
                    : null
                }
                { state.view === 'success' ?
                    <SuccessPanel state={state} setState={setState} />
                    : null
                }
                {state.view === 'error' ?
                    <ApiErrorPanel error={state.error}/>
                    :null
                }
            </div>
        </FuseAnimate>

    )
}

const ItemCard = ({state, setState}) => {
    const classes = useStyles();

    const sku = state.sku;

    const handleNew = () => {
        setState({...state, view: 'terms'})
    }

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center">
                <Card className={classes.card}>
                    <Typography variant="h6">Transfer Landline Number</Typography>
                    <hr className="my-2"/>

                    <div className="flex flex-row mx-auto my-6">
                        <div className="text-xl text-gray-500 pt-3">$</div>
                        <div className="text-8xl font-bold px-1">{sku.price/100}</div>
                    </div>

                    <hr/>

                    <div className="w-full py-4">
                        <ul className="list-disc mx-4">
                            <li className="pb-3">
                                This paid service will transfer landline
                                number <span className="font-bold whitespace-nowrap">{ausPhone(state.service.number)}</span> to another mobile number.
                            </li>
                            <li className="pb-3">
                                The mobile number you're transferring to must have an account already set up.
                            </li>
                            <li className="pb-3">
                                Any remaining call minutes will be transferred with the landline number.
                            </li>
                        </ul>
                    </div>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNew}
                    >
                        Transfer {ausPhone(state.service.number)}
                    </Button>
                </Card>
            </div>
        </FuseAnimate>
    )
}

const ReceivingAccountCard = ({state, setState}) => {
    const classes = useStyles();
    const focusRef = useRef();
    const {user} = useAuthUser();
    const {handleSubmit, control, formState:{ isValid, isSubmitting }} = useForm({ mode: 'onChange' });

    const onSubmit = (data, event) => {

        if (state.rxAccountError !== '') {
            setState({...state, rxAccountError:''});
        }

        return new Promise((resolve) => {
            const mobile = e164ify(data.mobileNumber);
            if (mobile === user.phone) {
                // User has entered their own phone number...
                setState({...state, rxAccountError: 'You have entered the mobile number for this account. '+
                        'Enter the mobile number for the receiving account.'})
                resolve();
                return
            }
            // Call the AccountService to create a transfer ticket.
            AccountService.transferService(user.accountId, state.service.id, data.mobileNumber, data.password)
                .then(result => {
                    setState({...state, mobileNumber: mobile, ticketId: result.ticketId, view:'checkout' })
                    resolve();
                })
                .catch(error => {
                    if (error.status === 401 || error.status === 403) {
                        setState({...state, mobileNumber: mobile, rxAccountError: 'bad_rx'});
                    } else {
                        setState({...state, mobileNumber: mobile, rxAccountError: "Sorry, we're not able to verify the receiving account right now. Please try again later."});
                    }
                    resolve();
                })
        })
    }

    const handleClickShowPassword = () => {
        setState({...state, showPassword: !state.showPassword});
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const validateMobile = (value) => {
        if (state.rxAccountError !== '') {
            setState({...state, rxAccountError:''});
        }
        return isValidMobile(value) || 'Enter the receiving mobile number'
    }

    return (
        <FuseAnimate animation="transition.expandIn" delay={100} complete={() => {
            focusRef.current && focusRef.current.focus();
        }}>
            <div className="flex flex-grow flex-col items-center">
                <Card className={classes.card}>
                    <Typography variant="h6">Receiving Account </Typography>
                    <hr className="my-2"/>
                    {/*<div className="uppercase text-xl font-bold mt-2">{sku.name}</div>*/}

                    <div className="my-6">
                        Enter the mobile number for the <strong>receiving account</strong>. After the
                        transfer is complete, calls to {ausPhone(state.service.number)} will go to the mobile number
                        you enter here.
                    </div>

                    { state.rxAccountError ?
                        <FuseAnimate animation="transition.expandIn" delay={100}>
                            <div className="flex flex-row rounded bg-red-200 p-3 mb-4">
                                <ErrorOutline fontSize="small" className="pr-1"/>
                                { state.rxAccountError === 'bad_rx' ?
                                    <div>
                                        We couldn't find an account with that mobile number and password.<br/>
                                        <ul className="list-disc ml-4">
                                            <li className="my-1">
                                                Confirm that you have created an account
                                                for {ausPhone(state.mobileNumber)}.
                                            </li>
                                            <li className="my-1">
                                                Check that you have entered the password for the receiving account correctly.
                                                You can tap the <VisibilityOff fontSize="small"/> icon to show the
                                                password.</li>
                                        </ul>
                                    </div>
                                    :
                                    <div>{state.rxAccountError}</div>
                                }
                            </div>
                        </FuseAnimate>
                        : null
                    }

                    <form
                        name="rxAccountForm"
                        noValidate
                        className="flex flex-col justify-center w-full"
                        onSubmit={handleSubmit((data, event) => onSubmit(data, event))}
                    >
                        <Controller
                            name='mobileNumber'
                            control={control}
                            defaultValue={state.mobileNumber}
                            rules={{
                                required: 'Your mobile number is required',
                                validate: (value) => validateMobile(value),
                            }}
                            render={ ({field: {onChange, value}, fieldState:{error} }) => (
                                <TextField
                                    label="Mobile Number"
                                    inputRef={focusRef}
                                    type="tel"
                                    value={value}
                                    onChange={onChange}
                                    variant="outlined"
                                    error={!!error}
                                    helperText={error ? error.message : 'Enter the receiving mobile phone number'}
                                    className={classes.formField}
                                    required
                                    fullWidth
                                />
                            )}
                        />

                        <Controller
                            name='password'
                            control={control}
                            defaultValue={state.password}
                            rules={{
                                required: 'Your password is required',
                            }}
                            render={ ({field: {onChange, value}, fieldState:{error} }) => (

                                <FormControl className={clsx(classes.margin, classes.formField)}
                                             variant="outlined"
                                             error={!!error}
                                             required={true}
                                >
                                    <InputLabel htmlFor="password">Password</InputLabel>
                                    <OutlinedInput
                                        name="password"
                                        // className={classes.loginField}
                                        label="Password"
                                        type={state.showPassword ? 'text' : 'password'}
                                        value={value}
                                        onChange={onChange}
                                        variant="outlined"
                                        required={true}
                                        fullWidth
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {state.showPassword ? <Visibility/> : <VisibilityOff/>}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                    <FormHelperText id="passwordHelperText">
                                        {error ? error.message : 'Enter the password for the receiving account'}
                                    </FormHelperText>
                                </FormControl>
                            )}
                        />

                        {/*<Button*/}
                        {/*    id="loginPasswordButton"*/}
                        {/*    type="submit"*/}
                        {/*    variant="contained"*/}
                        {/*    color="primary"*/}
                        {/*    className={classes.loginButton}*/}
                        {/*    aria-label="LOG IN"*/}
                        {/*    disabled={!isValid || isSubmitting}*/}
                        {/*>*/}
                        {/*    SIGN IN WITH PASSWORD*/}
                        {/*</Button>*/}

                        {/*<Button*/}
                        {/*    id="loginOTPButton"*/}
                        {/*    type="submit"*/}
                        {/*    variant="contained"*/}
                        {/*    color="primary"*/}
                        {/*    className={classes.loginButton}*/}
                        {/*    aria-label="LOG IN"*/}
                        {/*    disabled={!isValid || isSubmitting}*/}
                        {/*>*/}
                        {/*    {isSubmitting ? state.loginMethod === 'otp' ? 'Sending OTP...' : 'SIGN IN WITH ONE-TIME PASSWORD' : 'SIGN IN WITH ONE-TIME PASSWORD'}*/}
                        {/*</Button>*/}

                        {/*<div className="flex flex-col items-center justify-center pt-8 pb-3">*/}
                        {/*    <span className="font-medium">Don't have an account?</span>*/}
                        {/*    <Link className={classes.link} to="/signup">*/}
                        {/*        Create an account*/}
                        {/*    </Link>*/}
                        {/*</div>*/}

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!isValid || isSubmitting}
                            // onClick={handleNew}
                        >
                            CONTINUE
                        </Button>
                    </form>


                </Card>
            </div>
        </FuseAnimate>
    )
}


const CheckoutCard = ({state, setState}) => {
    const classes = useStyles();

    const {subtotal, taxes, total} = totals(state.sku);
    // let subtotal = 0;
    // let taxes = 0;
    // let total = 0;
    //
    // if (state.sku.taxInc) {
    //     total = state.sku.price/100;
    //     taxes = total - (total/((100+state.sku.taxRate)/100))
    //     subtotal = total - taxes;
    // } else {
    //     subtotal = state.sku.price/100
    //     taxes = Math.round(state.sku.taxRate * state.sku.price/100)/100
    //     total = subtotal + taxes
    // }

    const order = {
        sku: state.sku,
        serviceId: state.service.id,
        ticketId: state.ticketId,
        number: {number: state.service.number, zone: state.service.meta.zone},
        transferTo: state.mobileNumber,
        subtotal: subtotal,
        taxes: taxes,
        total: total,
    }

    // Get the global dispatch function
    const [, dispatch] = useStore();

    const onSuccess = (purchase) => {
        // the checkout completed successfully
        setState({...state, purchase, order, view: 'success'})

        // Invalidate the account in global store so we retrieve
        // the new service, dialActions, account status and paymentMethod if saved.
        dispatch(invalidateAccount())
    }

    const onError = (error) => {
        // the checkout failed

        // display error panel
        setState({...state, order, error, view:'error'})
    }

    // The checkout card basically wraps the
    // Checkout component, injecting the SKU to be charged.
    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center">
                <Card className={classes.card}>
                    <Checkout
                        order={order}
                        onSuccess={onSuccess}
                        onError={onError}
                    />
                </Card>
            </div>
        </FuseAnimate>
    )
}

const SuccessPanel = ({state, setState}) => {
    const classes = useStyles();

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load


    const number =  state.order.number.number;
    const toMobile = state.order.transferTo;
    const receiptUrl = state.purchase.purchase.receiptUrl;

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="flex flex-grow flex-col items-center">
                <Card className={classes.card}>
                <FuseAnimate animation="callout.bounce" delay={400}>
                    <div className="flex flex-col items-center h-16 my-6">
                        <CheckCircleOutline style={{fontSize: 64, color: 'green'}} />
                    </div>
                </FuseAnimate>
                {/*    <div className="flex flex-col items-center h-16 my-6">*/}
                <div className="text-base mb-3">
                    Thank you for your purchase!
                </div>
                <div className="text-base mb-3">
                    <span className="whitespace-nowrap font-bold">{ausPhone(number)}</span> has been successfully
                    transferred to <span className="whitespace-nowrap font-bold">{ausPhone(toMobile)}</span> and has
                    been removed from this account.
                </div>
                <ul className="list-disc mx-4">
                    <li className="pb-3">
                        Make sure <span className="whitespace-nowrap font-bold">{ausPhone(number)}</span> is added to
                        Contacts on <span className="whitespace-nowrap font-bold">{ausPhone(toMobile)}</span> so your
                        device knows you're expecting calls from the landline number.
                    </li>
                    { state.purchase.cardUpdated  ?
                        <li className="pb-3">Your card has been saved. You can change or remove your saved
                            card on the account page.</li>
                        : null
                    }
                </ul>

                <div className="my-2 w-full">
                    <Button aria-label="VIEW RECEIPT" className="w-full" color="primary" variant="outlined"
                            onClick={(e) => externalLink(e, receiptUrl, true)}>
                        VIEW RECEIPT
                    </Button>
                </div>

                {/*</div>*/}
                <div className="mt-4 w-full">
                    <Button variant="contained" color="primary" aria-label="NEXT" className="w-full" component={Link} to="/home">
                        RETURN TO THE DASHBOARD
                    </Button>
                </div>
                </Card>
            </div>
        </FuseAnimate>
    )
}

const ApiErrorPanel = ({error}) => {

    return (
        <FuseAnimate animation="transition.expandIn" delay={100}>
            <div className="p-4">
                <div className="flex justify-center text-6xl mt-1 mb-4">
                    <SentimentVeryDissatisfied fontSize="inherit" color="error" />
                </div>
                <Typography paragraph>
                    Sorry, it looks like there's a problem at our end. Please try again later.
                </Typography>
                <Typography variant="body2" color="textSecondary">{error.status}/{error.statusText}</Typography>
            </div>
        </FuseAnimate>
    )
}

export default TransferServicePage;
